<template>
  <div class="upcoming-buildings-page">
    <div class="page-header">
      <h3>Upcoming Building Installations</h3>
    </div>

    <div class="content-wrapper">
      <!-- Left Sidebar - Building List -->
      <div class="buildings-sidebar">
        <div v-if="loading" class="text-center p-3">
          <circle-loader :size="36"></circle-loader>
        </div>

        <div v-if="!loading && orders.length === 0" class="text-center p-3">
          <p class="text-muted">No upcoming installations found</p>
        </div>

        <div
          v-for="order in orders"
          :key="order.id"
          class="building-list-item"
          :class="{ active: selectedOrder && selectedOrder.id === order.id }"
          @click="selectOrder(order)"
        >
          <div class="building-item-header">
            <strong>{{ order.jobName }}</strong>
          </div>
          <div class="building-item-details">
            <div v-if="order.building">
              {{ order.building.shortId }}
              <span v-if="order.building.nickname">
                - {{ order.building.nickname }}
              </span>
            </div>
            <div class="text-muted small">
              {{ order.customer.name }}
            </div>
          </div>
        </div>
      </div>

      <!-- Main Content - Building Details -->
      <div class="building-details-section">
        <div v-if="!selectedOrder" class="empty-state">
          <p class="text-muted">
            Select a building from the list to view details
          </p>
        </div>

        <div v-if="selectedOrder && selectedOrder.building" class="details-content">
          <!-- Building Header -->
          <div class="details-header">
            <h4>
              {{ selectedOrder.building.shortId }}
              <span v-if="selectedOrder.building.nickname">
                - {{ selectedOrder.building.nickname }}
              </span>
            </h4>
            <div class="order-info">
              <span class="badge badge-primary">{{ selectedOrder.jobName }}</span>
              <span class="badge badge-secondary ml-2">{{ selectedOrder.status }}</span>
            </div>
          </div>

          <!-- Building Info -->
          <div class="info-section">
            <h5>Building Information</h5>
            <div class="info-grid">
              <div class="info-item">
                <label>Type:</label>
                <span>{{ selectedOrder.building.type }}</span>
              </div>
              <div class="info-item">
                <label>Dimensions:</label>
                <span v-if="selectedOrder.building.dimensions">
                  {{ selectedOrder.building.dimensions.width }}'W x
                  {{ selectedOrder.building.dimensions.length }}'L x
                  {{ selectedOrder.building.dimensions.height }}'H
                </span>
              </div>
              <div class="info-item">
                <label>Customer:</label>
                <span>{{ selectedOrder.customer.name }}</span>
              </div>
            </div>
          </div>

          <!-- Openings Info -->
          <div class="info-section">
            <h5>Openings</h5>
            <div class="openings-grid">
              <div class="opening-stat">
                <div class="stat-label">Roll Doors</div>
                <div class="stat-value">{{ rollDoorsCount }}</div>
              </div>
              <div class="opening-stat">
                <div class="stat-label">Man Doors</div>
                <div class="stat-value">{{ manDoorsCount }}</div>
              </div>
              <div class="opening-stat">
                <div class="stat-label">Windows</div>
                <div class="stat-value">{{ windowsCount }}</div>
              </div>
              <div class="opening-stat">
                <div class="stat-label">Total Openings</div>
                <div class="stat-value total">{{ totalOpenings }}</div>
              </div>
            </div>
          </div>

          <!-- Pricing Calculation -->
          <div class="pricing-section">
            <h5>Job Amount Calculation</h5>
            <div class="calculation-table">
              <div class="calc-row total-row">
                <label>Full Building Price:</label>
                <span class="amount">{{ fullPrice | currencyFilter }}</span>
              </div>

              <div class="calc-row subtract-row">
                <label>Sales Tax:</label>
                <span class="amount">- {{ salesTax | currencyFilter }}</span>
              </div>

              <div class="calc-row subtract-row">
                <label>Concrete Cost:</label>
                <span class="amount">- {{ concreteCost | currencyFilter }}</span>
              </div>

              <div class="calc-row subtract-row">
                <label>Grade-All Cost:</label>
                <span class="amount">- {{ gradeAllCost | currencyFilter }}</span>
              </div>

              <div class="calc-row divider"></div>

              <div class="calc-row final-row">
                <label><strong>Job Amount:</strong></label>
                <span class="amount"><strong>{{ jobAmount | currencyFilter }}</strong></span>
              </div>

              <div class="calc-row install-rate-row">
                <label>Job Install Rate (9% of Job Amount):</label>
                <span class="amount highlight">{{ jobInstallRate | currencyFilter }}</span>
              </div>
            </div>
          </div>

          <!-- Crew Calculator -->
          <div class="crew-calculator-section">
            <h5>Install Crew Calculator</h5>
            <div class="calculator-controls">
              <div class="control-group">
                <label for="crew-size">Crew Size:</label>
                <select id="crew-size" v-model.number="crewSize" class="form-control">
                  <option v-for="n in 10" :key="n" :value="n">{{ n }}</option>
                </select>
              </div>
              <div class="control-group">
                <label for="total-labor-hours">Total Labor Hours:</label>
                <input
                  id="total-labor-hours"
                  type="number"
                  v-model.number="totalLaborHours"
                  class="form-control"
                  min="1"
                  step="1"
                />
              </div>
            </div>

            <div class="calculator-results">
              <div class="result-card primary-result">
                <div class="result-label">Rate per Crew Member</div>
                <div class="result-value">{{ hourlyRatePerMember | currencyFilter }}/hr</div>
                <div class="result-breakdown">
                  <span>{{ dailyRatePerMember | currencyFilter }}/day</span>
                  <span class="separator">•</span>
                  <span>{{ totalPerCrewMember | currencyFilter }} total</span>
                </div>
              </div>
              <div class="result-card">
                <div class="result-label">Total Days to Complete</div>
                <div class="result-value">{{ totalDays }} {{ totalDays === 1 ? 'day' : 'days' }}</div>
                <div class="result-note">({{ hoursPerDay }} hours/day)</div>
              </div>
              <div class="result-card comparison-card" :class="{ positive: rateVsTarget >= 0, negative: rateVsTarget < 0 }">
                <div class="result-label">vs. Target $25/hr</div>
                <div class="result-value">{{ rateVsTarget >= 0 ? '+' : '' }}{{ rateVsTarget | currencyFilter }}/hr</div>
                <div class="result-note">{{ rateVsTarget >= 0 ? 'Above' : 'Below' }} target</div>
              </div>
            </div>
          </div>

          <!-- Bonus Distribution Calculator -->
          <div v-if="selectedOrder && selectedOrder.building" class="bonus-distribution-section">
            <h5>Bonus Distribution (Above $25/hr Target)</h5>
            <div class="bonus-info-bar">
              <div class="bonus-pool-display">
                <span class="label">Total Bonus Pool:</span>
                <span class="value">{{ totalBonusPool | currencyFilter }}</span>
              </div>
              <div class="bonus-per-hour">
                <span class="label">Bonus per Hour:</span>
                <span class="value">+{{ bonusPerHour | currencyFilter }}/hr</span>
              </div>
            </div>

            <div class="crew-distribution-table">
              <div class="table-header">
                <div class="col-member">Member</div>
                <div class="col-rate">Base Rate</div>
                <div class="col-base-amount">Base Amount</div>
                <div class="col-percentage">Bonus %</div>
                <div class="col-bonus">Bonus Amount</div>
                <div class="col-total">Total Earnings</div>
              </div>

              <div 
                v-for="(member, index) in crewMembers" 
                :key="index"
                class="table-row"
              >
                <div class="col-member">
                  <strong>Crew {{ index + 1 }}</strong>
                </div>
                <div class="col-rate">
                  <input
                    type="number"
                    v-model.number="member.baseRate"
                    class="rate-input"
                    min="0"
                    step="0.5"
                  />
                  <span class="unit">/hr</span>
                </div>
                <div class="col-base-amount">
                  <span class="base-amount-value">{{ getMemberBaseAmount(member) | currencyFilter }}</span>
                </div>
                <div class="col-percentage">
                  <input
                    type="number"
                    v-model.number="member.bonusPercentage"
                    class="percentage-input"
                    min="0"
                    max="100"
                    step="1"
                    @input="normalizeBonusPercentages"
                  />
                  <span class="unit">%</span>
                </div>
                <div class="col-bonus">
                  <span class="bonus-value">{{ getMemberBonus(member) | currencyFilter }}</span>
                </div>
                <div class="col-total">
                  <span class="total-value">{{ getMemberTotal(member) | currencyFilter }}</span>
                </div>
              </div>

              <div class="table-footer">
                <div class="col-member"></div>
                <div class="col-rate"></div>
                <div class="col-base-amount">
                  <strong>{{ totalBaseAmount | currencyFilter }}</strong>
                </div>
                <div class="col-percentage">
                  <strong>{{ totalBonusPercentage }}%</strong>
                </div>
                <div class="col-bonus">
                  <strong>{{ totalDistributedBonus | currencyFilter }}</strong>
                </div>
                <div class="col-total">
                  <strong>{{ totalCrewEarnings | currencyFilter }}</strong>
                </div>
              </div>
            </div>

            <button 
              @click="resetBonusDistribution" 
              class="btn btn-sm btn-outline-secondary mt-3"
            >
              Reset to Equal Distribution
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import CircleLoader from '@/components/CircleLoader.vue'

export default {
  name: 'UpcomingBuildingsPage',
  components: {
    CircleLoader,
  },
  data() {
    return {
      orders: [],
      selectedOrder: null,
      loading: true,
      crewSize: 4,
      totalLaborHours: 0,
      hoursPerDay: 8,
      targetHourlyRate: 25,
      crewMembers: [],
    }
  },
  watch: {
    crewSize(newSize, oldSize) {
      this.initializeCrewMembers()
    },
  },
  computed: {
    fullPrice() {
      if (!this.selectedOrder || !this.selectedOrder.building) return 0
      const building = this.selectedOrder.building
      // Use kitAndInstall total as the full price since install is included
      return building.price?.kitAndInstall?.total || 0
    },
    salesTax() {
      if (!this.selectedOrder || !this.selectedOrder.building) return 0
      const building = this.selectedOrder.building
      return building.price?.kitAndInstall?.salesTax || 0
    },
    concreteCost() {
      if (!this.selectedOrder || !this.selectedOrder.building) return 0
      const building = this.selectedOrder.building
      return building.foundation?.price?.total || 0
    },
    gradeAllCost() {
      if (!this.selectedOrder || !this.selectedOrder.building) return 0
      const building = this.selectedOrder.building
      
      // Look for Grade-All in addOnItems
      if (building.addOnItems && Array.isArray(building.addOnItems)) {
        const gradeAllItem = building.addOnItems.find(item => 
          item.name && item.name.toLowerCase().includes('grade')
        )
        if (gradeAllItem && gradeAllItem.total) {
          return gradeAllItem.total
        }
      }
      
      // Also check installAddOnItems
      if (building.installAddOnItems && Array.isArray(building.installAddOnItems)) {
        const gradeAllItem = building.installAddOnItems.find(item => 
          item.name && item.name.toLowerCase().includes('grade')
        )
        if (gradeAllItem && gradeAllItem.total) {
          return gradeAllItem.total
        }
      }
      
      // Check manualAddOnItems as well
      if (building.manualAddOnItems && Array.isArray(building.manualAddOnItems)) {
        const gradeAllItem = building.manualAddOnItems.find(item => 
          item.name && item.name.toLowerCase().includes('grade')
        )
        if (gradeAllItem && gradeAllItem.total) {
          return gradeAllItem.total
        }
      }
      
      return 0
    },
    jobAmount() {
      return this.fullPrice - this.salesTax - this.concreteCost - this.gradeAllCost
    },
    jobInstallRate() {
      return this.jobAmount * 0.09
    },
    rollDoorsCount() {
      if (!this.selectedOrder || !this.selectedOrder.building) return 0
      const building = this.selectedOrder.building
      
      let count = 0
      
      // Check rollDoors array (main location)
      if (building.rollDoors && Array.isArray(building.rollDoors)) {
        building.rollDoors.forEach(door => {
          count += door.quantity || 0
        })
      }
      
      // Also check openings array for Roll Door type
      if (building.openings && Array.isArray(building.openings)) {
        building.openings.forEach(opening => {
          if (opening.type === 'Roll Door') {
            count += opening.quantity || 0
          }
        })
      }
      
      return count
    },
    manDoorsCount() {
      if (!this.selectedOrder || !this.selectedOrder.building) return 0
      const building = this.selectedOrder.building
      
      let count = 0
      
      // Check manDoors object (has quantity property directly)
      if (building.manDoors && building.manDoors.quantity) {
        count += building.manDoors.quantity
      }
      
      // Check manDoorList array
      if (building.manDoorList && Array.isArray(building.manDoorList)) {
        building.manDoorList.forEach(door => {
          count += door.quantity || 0
        })
      }
      
      // Also check openings array for Man Door type
      if (building.openings && Array.isArray(building.openings)) {
        building.openings.forEach(opening => {
          if (opening.type === 'Man Door') {
            count += opening.quantity || 0
          }
        })
      }
      
      return count
    },
    windowsCount() {
      if (!this.selectedOrder || !this.selectedOrder.building) return 0
      const building = this.selectedOrder.building
      
      let count = 0
      
      // Check windows object
      const windows = building.windows || {}
      count += (windows.withGrid || 0) + (windows.withoutGrid || 0)
      
      // Check windowList array
      if (building.windowList && Array.isArray(building.windowList)) {
        building.windowList.forEach(window => {
          count += window.quantity || 0
        })
      }
      
      // Also check openings array for Window type
      if (building.openings && Array.isArray(building.openings)) {
        building.openings.forEach(opening => {
          if (opening.type === 'Window') {
            count += opening.quantity || 0
          }
        })
      }
      
      return count
    },
    totalOpenings() {
      return this.rollDoorsCount + this.manDoorsCount + this.windowsCount
    },
    hourlyRatePerMember() {
      if (this.totalLaborHours === 0 || this.crewSize === 0) return 0
      return this.jobInstallRate / this.totalLaborHours
    },
    hoursPerCrewMember() {
      if (this.crewSize === 0) return 0
      return this.totalLaborHours / this.crewSize
    },
    totalDays() {
      if (this.hoursPerCrewMember === 0 || this.hoursPerDay === 0) return 0
      return Math.ceil(this.hoursPerCrewMember / this.hoursPerDay)
    },
    rateVsTarget() {
      return this.hourlyRatePerMember - this.targetHourlyRate
    },
    totalPerCrewMember() {
      if (this.crewSize === 0) return 0
      return this.jobInstallRate / this.crewSize
    },
    dailyRatePerMember() {
      return this.hourlyRatePerMember * this.hoursPerDay
    },
    totalBaseAmount() {
      return this.crewMembers.reduce((sum, member) => sum + this.getMemberBaseAmount(member), 0)
    },
    totalBonusPool() {
      // Bonus pool = Job Install Rate - Total Base Amount
      return Math.max(0, this.jobInstallRate - this.totalBaseAmount)
    },
    bonusPerHour() {
      if (this.totalLaborHours === 0) return 0
      return this.totalBonusPool / this.totalLaborHours
    },
    totalBonusPercentage() {
      return this.crewMembers.reduce((sum, member) => sum + (member.bonusPercentage || 0), 0)
    },
    totalDistributedBonus() {
      return this.crewMembers.reduce((sum, member) => sum + this.getMemberBonus(member), 0)
    },
    totalCrewEarnings() {
      return this.crewMembers.reduce((sum, member) => sum + this.getMemberTotal(member), 0)
    },
  },
  mounted() {
    this.fetchOrders()
  },
  methods: {
    async fetchOrders() {
      try {
        this.loading = true
        const response = await axios.get('/services/orders/upcoming-installs')
        this.orders = response.data.orders
      } catch (error) {
        console.error('Error fetching upcoming installs:', error)
        this.$toasted.error('Error loading upcoming installations', {
          duration: 3000,
        })
      } finally {
        this.loading = false
      }
    },
    selectOrder(order) {
      this.selectedOrder = order
      // Calculate default total labor hours targeting $25/hr per crew member
      if (this.jobInstallRate > 0) {
        this.totalLaborHours = Math.ceil(this.jobInstallRate / this.targetHourlyRate)
      }
      this.initializeCrewMembers()
    },
    initializeCrewMembers() {
      const equalPercentage = Math.floor(100 / this.crewSize)
      const remainder = 100 - (equalPercentage * this.crewSize)
      
      this.crewMembers = []
      for (let i = 0; i < this.crewSize; i++) {
        this.crewMembers.push({
          baseRate: this.targetHourlyRate,
          bonusPercentage: i === 0 ? equalPercentage + remainder : equalPercentage,
        })
      }
    },
    resetBonusDistribution() {
      this.initializeCrewMembers()
    },
    normalizeBonusPercentages() {
      // Allow user to manually adjust, just warn if not 100%
      // Optional: Auto-adjust to keep at 100%
    },
    getMemberBaseAmount(member) {
      const hoursPerMember = this.totalLaborHours / this.crewSize
      return (member.baseRate || this.targetHourlyRate) * hoursPerMember
    },
    getMemberBonus(member) {
      if (!this.totalBonusPool) return 0
      return (this.totalBonusPool * (member.bonusPercentage || 0)) / 100
    },
    getMemberTotal(member) {
      return this.getMemberBaseAmount(member) + this.getMemberBonus(member)
    },
  },
}
</script>

<style lang="scss" scoped>
.upcoming-buildings-page {
  padding: 20px;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.page-header {
  margin-bottom: 20px;
}

.content-wrapper {
  display: flex;
  gap: 20px;
  flex: 1;
  overflow: hidden;
}

.buildings-sidebar {
  width: 350px;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 10px;
  overflow-y: auto;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.building-list-item {
  background: white;
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transform: translateY(-1px);
  }

  &.active {
    border-color: #007bff;
    background: #e7f3ff;
  }
}

.building-item-header {
  margin-bottom: 6px;
  color: #333;
}

.building-item-details {
  font-size: 14px;
  color: #666;
}

.building-details-section {
  flex: 1;
  background: white;
  border-radius: 8px;
  padding: 24px;
  overflow-y: auto;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 16px;
}

.details-content {
  max-width: 800px;
}

.details-header {
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 2px solid #e9ecef;

  h4 {
    margin-bottom: 10px;
    color: #333;
  }
}

.order-info {
  .badge {
    font-size: 12px;
    padding: 6px 12px;
  }
}

.info-section {
  margin-bottom: 30px;

  h5 {
    color: #495057;
    margin-bottom: 15px;
    font-size: 18px;
  }
}

.info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.info-item {
  display: flex;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;

  label {
    font-weight: 600;
    min-width: 120px;
    color: #495057;
    margin-bottom: 0;
  }

  span {
    color: #212529;
  }
}

.pricing-section {
  h5 {
    color: #495057;
    margin-bottom: 15px;
    font-size: 18px;
  }
}

.calculation-table {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
}

.calc-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  font-size: 16px;

  label {
    margin-bottom: 0;
  }

  &.total-row {
    color: #212529;
    font-size: 18px;
  }

  &.subtract-row {
    color: #6c757d;
    padding-left: 20px;

    .amount {
      color: #dc3545;
    }
  }

  &.divider {
    border-top: 2px solid #dee2e6;
    margin: 10px 0;
    padding: 0;
  }

  &.final-row {
    font-size: 20px;
    color: #28a745;
    padding-top: 15px;
  }
}

.amount {
  font-family: 'Courier New', monospace;
  min-width: 120px;
  text-align: right;
}

.install-rate-row {
  margin-top: 10px;
  padding-top: 15px;
  border-top: 1px solid #dee2e6;
  color: #17a2b8;
  font-size: 17px;

  .highlight {
    color: #17a2b8;
    font-weight: 600;
  }
}

.openings-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
}

.opening-stat {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 15px;
  text-align: center;
  transition: all 0.2s;

  &:hover {
    border-color: #007bff;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
  }
}

.stat-label {
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 8px;
  font-weight: 500;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #495057;

  &.total {
    color: #007bff;
  }
}

.crew-calculator-section {
  margin-top: 30px;
  padding-top: 30px;
  border-top: 3px solid #e9ecef;

  h5 {
    color: #495057;
    margin-bottom: 20px;
    font-size: 18px;
  }
}

.calculator-controls {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 25px;
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
}

.control-group {
  display: flex;
  flex-direction: column;

  label {
    font-weight: 600;
    margin-bottom: 8px;
    color: #495057;
    font-size: 14px;
  }

  .form-control {
    padding: 10px 15px;
    font-size: 16px;
    border: 2px solid #dee2e6;
    border-radius: 6px;
    transition: border-color 0.2s;

    &:focus {
      border-color: #007bff;
      box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
      outline: none;
    }
  }

  select.form-control {
    cursor: pointer;
    padding-top: 6px;
    padding-right: 35px;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
  }
}

.calculator-results {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
}

.result-card {
  background: white;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  &.primary-result {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;

    .result-label {
      color: rgba(255, 255, 255, 0.9);
    }

    .result-value {
      color: white;
      font-size: 32px;
    }

    .result-breakdown {
      color: rgba(255, 255, 255, 0.95);
      font-size: 16px;
      font-weight: 600;
      margin-top: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;

      .separator {
        opacity: 0.5;
        font-size: 14px;
      }
    }
  }

  &.comparison-card {
    &.positive {
      background: linear-gradient(135deg, #56ab2f 0%, #a8e063 100%);
      color: white;
      border: none;

      .result-label {
        color: rgba(255, 255, 255, 0.9);
      }

      .result-value {
        color: white;
      }

      .result-note {
        color: rgba(255, 255, 255, 0.8);
      }
    }

    &.negative {
      background: linear-gradient(135deg, #eb3349 0%, #f45c43 100%);
      color: white;
      border: none;

      .result-label {
        color: rgba(255, 255, 255, 0.9);
      }

      .result-value {
        color: white;
      }

      .result-note {
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }
}

.result-label {
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.result-value {
  font-size: 28px;
  font-weight: 700;
  color: #212529;
  margin-bottom: 5px;
}

.result-note {
  font-size: 12px;
  color: #6c757d;
  font-style: italic;
  margin-top: 5px;
}

.bonus-distribution-section {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 3px solid #e9ecef;

  h5 {
    color: #495057;
    margin-bottom: 20px;
    font-size: 18px;
  }
}

.bonus-info-bar {
  display: flex;
  justify-content: space-around;
  gap: 20px;
  margin-bottom: 25px;
  padding: 15px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  color: white;

  .bonus-pool-display,
  .bonus-per-hour {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;

    .label {
      font-size: 13px;
      opacity: 0.9;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .value {
      font-size: 24px;
      font-weight: 700;
    }
  }
}

.crew-distribution-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  .table-header,
  .table-row,
  .table-footer {
    display: grid;
    grid-template-columns: 100px 140px 130px 120px 130px 140px;
    gap: 10px;
    align-items: center;
    padding: 12px 15px;
  }

  .table-header {
    background: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
    font-weight: 600;
    font-size: 13px;
    color: #495057;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .table-row {
    border-bottom: 1px solid #e9ecef;
    transition: background-color 0.2s;

    &:hover {
      background: #f8f9fa;
    }

    &:last-of-type {
      border-bottom: 2px solid #dee2e6;
    }
  }

  .table-footer {
    background: #f8f9fa;
    font-weight: 600;
    color: #495057;
  }

  .col-member {
    strong {
      color: #667eea;
      font-size: 15px;
    }
  }

  .col-rate,
  .col-percentage {
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .rate-input,
  .percentage-input {
    width: 80px;
    padding: 6px 10px;
    border: 2px solid #dee2e6;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 600;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: #667eea;
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }
  }

  .unit {
    font-size: 13px;
    color: #6c757d;
    font-weight: 500;
  }

  .base-amount-value {
    color: #6c757d;
    font-weight: 600;
    font-size: 15px;
  }

  .bonus-value {
    color: #28a745;
    font-weight: 600;
    font-size: 15px;
  }

  .total-value {
    color: #495057;
    font-weight: 700;
    font-size: 16px;
  }
}
</style>

